import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuthContext } from "../context/AuthContext";
import { useChatUnread } from "../context/ChatUnreadContext";
import { Link } from "react-router-dom";
import "../styles/DashboardSidebar.css";
import useMailStore from "../stores/useMailStore";

const DashboardSidebar = ({ isCollapsed, setIsCollapsed }) => {
  const [openSubmenu, setOpenSubmenu] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
  });

  const unreadTotal = useMailStore((state) => state.unreadTotal);
  const fetchUnreadTotal = useMailStore((state) => state.fetchUnreadTotal);

  const { user: userInfo } = useAuthContext();
  const { unreadCount } = useChatUnread();

  const pollingRef = useRef(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const savedSubmenuState = localStorage.getItem("openSubmenu");
    if (savedSubmenuState) {
      setOpenSubmenu(JSON.parse(savedSubmenuState));
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const fetchUnreadTotalSafe = useCallback(async () => {
    if (isFetchingRef.current) {
      console.log("[DashboardSidebar] skip fetchUnreadTotal (still fetching)");
      return;
    }

    isFetchingRef.current = true;

    try {
      await fetchUnreadTotal();
    } finally {
      isFetchingRef.current = false;
    }
  }, [fetchUnreadTotal]);

  useEffect(() => {
    stopPolling();

    let cancelled = false;

    const loop = async () => {
      if (cancelled) return;

      await fetchUnreadTotalSafe();

      if (cancelled) return;
      pollingRef.current = setTimeout(loop, 30000);
    };

    // 初回はすぐ取得
    loop();

    return () => {
      cancelled = true;
      stopPolling();
      isFetchingRef.current = false;
    };
  }, [fetchUnreadTotalSafe, stopPolling]);

  const toggleSubmenu = (index) => {
    const newSubmenuState = { ...openSubmenu, [index]: !openSubmenu[index] };
    setOpenSubmenu(newSubmenuState);
    localStorage.setItem("openSubmenu", JSON.stringify(newSubmenuState));
  };

  // 折りたたみ時に表示するアイコンメニュー
  const collapsedGroups = [
    {
      id: 1,
      label: "商品管理",
      icon: "🗂️",
      items: [
        { label: "在庫一覧", icon: "📦", to: "/inventory_list" },
        { label: "入庫処理", icon: "📥", to: "/stock_in" },
        { label: "作品名・メーカー名の統一", icon: "🧹", to: "/inventory_cleanup" },
      ],
    },
    {
      id: 2,
      label: "登録管理",
      icon: "📝",
      items: [
        { label: "Excel一括登録", icon: "📊", to: "/upload_excel" },
        { label: "WEB登録まとめて", icon: "🌐", to: "/scrape_all" },
        { label: "WEB登録個別", icon: "🔎", to: "/scrape_single" },
      ],
    },
    {
      id: 6,
      label: "販売管理",
      icon: "💹",
      items: [
        { label: "販売履歴", icon: "📈", to: "/SalesSummaryPage" },
      ],
    },
    {
      id: 3,
      label: "買取管理",
      icon: "🤝",
      items: [
        { label: "買取処理状況一覧", icon: "📋", to: "/PurchaseOrderSearch" },
        { label: "管理画面・メール", icon: "📧", to: "/Web" },
      ],
    },
    {
      id: 4,
      label: "POSレジシステム",
      icon: "🧾",
      items: [
        { label: "HOME画面", icon: "🛒", to: "/RegisterPage" },
      ],
    },
    {
      id: 5,
      label: "Utilities / Tools",
      icon: "🛠️",
      items: [
        { label: "シフト", icon: "📅", to: "/shift_management" },

        ...(!userInfo?.is_staff
          ? [{ label: "免税分析", icon: "🛂", to: "/tax_exempt_report" }]
          : []),

        { label: "各種設定", icon: "⚙️", to: "/printer_settings" },
      ],
    },

    ...(userInfo?.is_staff
      ? [
          {
            id: 7,
            label: "管理画面",
            icon: "🔐",
            items: [
              { label: "日別売上一覧", icon: "💰", to: "/daily_sales" },
              { label: "免税分析", icon: "🛂", to: "/tax_exempt_report" },
              { label: "各種登録/処理", icon: "🔧", to: "/backendtaskpanel" },
              {
                label: "管理画面にアクセス",
                icon: "🖥️",
                href: "http://localhost:8080/admin/",
              },
            ],
          },
        ]
      : []),
  ];

  // 折りたたみ状態からカテゴリを開く
  const expandGroup = (index) => {
    const newSubmenuState = {
      ...openSubmenu,
      [index]: true,
    };

    setOpenSubmenu(newSubmenuState);

    localStorage.setItem(
      "openSubmenu",
      JSON.stringify(newSubmenuState)
    );

    setIsCollapsed(false);
  };

  return (
    <div className={`dashboard-sidebar-container ${isCollapsed ? "collapsed" : ""}`}>
      <div className="dashboard-sidebar-logo">
        <div className={`dashboard-icon ${unreadCount > 0 ? "chat-unread" : ""}`}>
          <button className="collapse-toggle-button" onClick={() => setIsCollapsed(!isCollapsed)}>
            {isCollapsed ? "▶️" : <img src={`/media/system_images/freelen_mimic.png`} alt="" />}
          </button>
          {!isCollapsed && "ダッシュボード"}
        </div>

        {!isCollapsed && (
          <div className={`chat-info ${unreadCount > 0 ? "chat-unread" : ""}`}>
            <div className={`marin-wrapper ${unreadCount > 0 ? "has-unread" : ""}`}>
              <img src="/media/system_images/marin_info.png" alt="新着" />

              {unreadCount > 0 && (
                <img
                  src="/media/system_images/unread_text.png"
                  className="unread-text-img"
                  alt=""
                />
              )}
            </div>
          </div>
        )}
      </div>

      <ul className="dashboard-sidebar-menu">

          {/* 折りたたみ時のアイコンメニュー */}
          {isCollapsed ? (
            <>
              {/* ホーム */}
              <li className="dashboard-sidebar-compact-home">
                <Link
                  to="/"
                  className="dashboard-sidebar-compact-link"
                  title="ホーム"
                  aria-label="ホーム"
                >
                  <span>🏠</span>
                </Link>
              </li>

              {/* 全カテゴリー */}
              {collapsedGroups.map((group) => (
                <li
                  key={group.id}
                  className="dashboard-sidebar-compact-group"
                >

                  
                  {/* カテゴリー内の各アイコン */}
                  <ul className="dashboard-sidebar-compact-items">

                    {group.items.map((item) => (
                      <li key={item.label}>

                        {item.href ? (

                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dashboard-sidebar-compact-link"
                            title={item.label}
                            aria-label={item.label}
                          >
                            <span>{item.icon}</span>
                          </a>

                        ) : (

                          <Link
                            to={item.to}
                            className="dashboard-sidebar-compact-link"
                            title={item.label}
                            aria-label={item.label}
                          >
                            <span>{item.icon}</span>
                          </Link>

                        )}

                      </li>
                    ))}

                  </ul>
                </li>
              ))}
            </>
          ) : (
            <li>
              <Link to="/">ホーム</Link>
            </li>
          )}

          {/* 通常表示のメニュー */}
          {!isCollapsed && (
          <>
            <li className="dashboard-sidebar-has-submenu">
              <button onClick={() => toggleSubmenu(1)}>商品管理 ▼</button>
              <ul className={`dashboard-sidebar-submenu ${openSubmenu[1] ? "show" : ""}`}>
                <li><Link to="/inventory_list">在庫一覧</Link></li>
                <li><Link to="/stock_in">入庫処理</Link></li>
                <li><Link to="/inventory_cleanup">作品名・メーカー名の統一</Link></li>
              </ul>
            </li>

            <li className="dashboard-sidebar-has-submenu">
              <button onClick={() => toggleSubmenu(2)}>登録管理 ▼</button>
              <ul className={`dashboard-sidebar-submenu ${openSubmenu[2] ? "show" : ""}`}>
                <li><Link to="/upload_excel">Excel一括登録</Link></li>
                <li><Link to="/scrape_all">WEB登録まとめて</Link></li>
                <li><Link to="/scrape_single">WEB登録個別</Link></li>
              </ul>
            </li>

            <li className="dashboard-sidebar-has-submenu">
              <button onClick={() => toggleSubmenu(6)}>販売管理 ▼</button>
              <ul className={`dashboard-sidebar-submenu ${openSubmenu[6] ? "show" : ""}`}>
                <li><Link to="/SalesSummaryPage">販売履歴</Link></li>
              </ul>
            </li>

            <li className="dashboard-sidebar-has-submenu">
              <button onClick={() => toggleSubmenu(3)}>買取管理 ▼</button>
              <ul className={`dashboard-sidebar-submenu ${openSubmenu[3] ? "show" : ""}`}>
                <li><Link to="/PurchaseOrderSearch">買取処理状況一覧</Link></li>
                <li>
                  <Link to="/Web">
                    管理画面・メール
                  </Link>
                </li>
              </ul>
            </li>

            <li className="dashboard-sidebar-has-submenu">
              <button onClick={() => toggleSubmenu(4)}>POSレジシステム ▼</button>
              <ul className={`dashboard-sidebar-submenu ${openSubmenu[4] ? "show" : ""}`}>
                <li><Link to="/RegisterPage">HOME画面</Link></li>
              </ul>
            </li>

            <li className="dashboard-sidebar-has-submenu">
              <button onClick={() => toggleSubmenu(5)}>Utilities / Tools ▼</button>
              <ul className={`dashboard-sidebar-submenu ${openSubmenu[5] ? "show" : ""}`}>
                <li><Link to="/shift_management"> シフト</Link></li>
                {!userInfo?.is_staff && (
                <li><Link to="/tax_exempt_report"> 免税分析</Link></li>
                )}
                <li><Link to="/printer_settings"> ⚙各種設定</Link></li>
              </ul>
            </li>

            {userInfo?.is_staff && (
              <li className="dashboard-sidebar-has-submenu">
                <button onClick={() => toggleSubmenu(7)}>管理画面 ▼</button>
                <ul className={`dashboard-sidebar-submenu ${openSubmenu[7] ? "show" : ""}`}>
                  <li><Link to="/daily_sales"> 日別売上一覧</Link></li>
                  <li><Link to="/tax_exempt_report"> 免税分析</Link></li>
                  <li><Link to="/backendtaskpanel">各種登録/処理</Link></li>
                  <li>
                    <a href="http://localhost:8080/admin/" target="_blank" rel="noopener noreferrer">
                      管理画面にアクセス
                    </a>
                  </li>
                </ul>
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

export default DashboardSidebar;